import React, { useState } from 'react';
import { Sparkles, Send, Loader2, CheckCircle, RefreshCw, Eye, FileText, Settings, Wand2, Copy, Check } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AIDraftGenerator = ({ caseData, onAnswerSent }) => {
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [draft, setDraft] = useState('');
  const [editedDraft, setEditedDraft] = useState('');
  const [generated, setGenerated] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [tone, setTone] = useState('professional'); // professional, friendly, formal
  const [length, setLength] = useState('medium'); // short, medium, long
  const [copied, setCopied] = useState(false);

  const generateDraft = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/marketplace/generate-draft`, {
        case_id: caseData.id,
        topic: caseData.topic,
        intake_summary: caseData.intake_summary,
        complexity: caseData.complexity,
        tone: tone,
        length: length
      });
      
      const generatedText = response.data.draft;
      setDraft(generatedText);
      setEditedDraft(generatedText);
      setGenerated(true);
      toast.success('AI-utkast generert!');
    } catch (error) {
      console.error('Error generating draft:', error);
      toast.error('Kunne ikke generere utkast');
    } finally {
      setLoading(false);
    }
  };

  const sendAnswer = async () => {
    if (!editedDraft.trim()) {
      toast.error('Svaret kan ikke være tomt');
      return;
    }

    setSending(true);
    try {
      await axios.post(`${API}/marketplace/send-answer`, {
        case_id: caseData.id,
        answer_text: editedDraft,
        ai_draft: draft
      });
      
      toast.success('Svar sendt til kunde!');
      if (onAnswerSent) onAnswerSent();
    } catch (error) {
      console.error('Error sending answer:', error);
      toast.error('Kunne ikke sende svar');
    } finally {
      setSending(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(editedDraft);
    setCopied(true);
    toast.success('Kopiert til utklippstavle!');
    setTimeout(() => setCopied(false), 2000);
  };

  const wordCount = editedDraft.split(/\s+/).filter(w => w.length > 0).length;

  return (
    <div className="h-full flex flex-col bg-gray-50">
      
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200 bg-white">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-gray-900 to-gray-700 rounded-xl flex items-center justify-center shadow-md">
            <Wand2 className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">AI Draft Generator</h2>
            <p className="text-sm text-gray-600">Generer profesjonelle juridiske utkast med AI</p>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        
        {/* Case Info Card */}
        <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-gray-600" />
              <h3 className="text-base font-bold text-gray-900">Saksinformasjon</h3>
            </div>
            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
              caseData.complexity === 'COMPLEX' ? 'bg-purple-50 text-purple-700 border border-purple-200' : 
              caseData.complexity === 'SIMPLE' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' :
              'bg-orange-50 text-orange-700 border border-orange-200'
            }`}>
              {caseData.complexity === 'COMPLEX' ? 'Kompleks' : 
               caseData.complexity === 'SIMPLE' ? 'Enkel' : 'Gjennomgang'}
            </span>
          </div>
          <h4 className="font-semibold text-gray-900 mb-2">{caseData.topic}</h4>
          <p className="text-sm text-gray-600 leading-relaxed">{caseData.intake_summary}</p>
        </div>

        {/* Settings */}
        {!generated && (
          <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
            <div className="flex items-center gap-2 mb-4">
              <Settings className="w-5 h-5 text-gray-600" />
              <h3 className="text-base font-bold text-gray-900">Innstillinger</h3>
            </div>
            
            <div className="space-y-4">
              {/* Tone */}
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">Tone</label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { id: 'professional', label: 'Profesjonell', desc: 'Balansert og seriøs' },
                    { id: 'friendly', label: 'Vennlig', desc: 'Personlig og tilgjengelig' },
                    { id: 'formal', label: 'Formell', desc: 'Streng og offisiell' }
                  ].map(option => (
                    <button
                      key={option.id}
                      onClick={() => setTone(option.id)}
                      className={`p-3 rounded-xl border-2 text-left transition-all ${
                        tone === option.id
                          ? 'border-gray-900 bg-gray-900 text-white shadow-md'
                          : 'border-gray-200 bg-white hover:border-gray-300'
                      }`}
                    >
                      <div className={`text-sm font-semibold mb-1 ${
                        tone === option.id ? 'text-white' : 'text-gray-900'
                      }`}>
                        {option.label}
                      </div>
                      <div className={`text-xs ${
                        tone === option.id ? 'text-gray-300' : 'text-gray-500'
                      }`}>
                        {option.desc}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Length */}
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">Lengde</label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { id: 'short', label: 'Kort', desc: '~150 ord' },
                    { id: 'medium', label: 'Medium', desc: '~300 ord' },
                    { id: 'long', label: 'Lang', desc: '~500 ord' }
                  ].map(option => (
                    <button
                      key={option.id}
                      onClick={() => setLength(option.id)}
                      className={`p-3 rounded-xl border-2 text-left transition-all ${
                        length === option.id
                          ? 'border-gray-900 bg-gray-900 text-white shadow-md'
                          : 'border-gray-200 bg-white hover:border-gray-300'
                      }`}
                    >
                      <div className={`text-sm font-semibold mb-1 ${
                        length === option.id ? 'text-white' : 'text-gray-900'
                      }`}>
                        {option.label}
                      </div>
                      <div className={`text-xs ${
                        length === option.id ? 'text-gray-300' : 'text-gray-500'
                      }`}>
                        {option.desc}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Generate Button */}
        {!generated && (
          <div className="text-center py-8">
            <div className="w-20 h-20 bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-md">
              <Sparkles className="w-10 h-10 text-gray-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">
              Klar til å generere utkast
            </h3>
            <p className="text-sm text-gray-600 mb-6 max-w-md mx-auto">
              AI-en vil analysere saken og lage et profesjonelt juridisk svar basert på dine valg.
            </p>
            <button
              onClick={generateDraft}
              disabled={loading}
              className="px-8 py-3 bg-gray-900 hover:bg-gray-800 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Genererer utkast...
                </>
              ) : (
                <>
                  <Sparkles className="w-5 h-5" />
                  Generer AI-utkast
                </>
              )}
            </button>
          </div>
        )}

        {/* Draft Editor */}
        {generated && (
          <div className="space-y-4">
            {/* Editor Header */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <h3 className="text-base font-bold text-gray-900">Rediger utkast</h3>
                <span className="flex items-center gap-1.5 px-2.5 py-1 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full text-xs font-semibold">
                  <CheckCircle className="w-3.5 h-3.5" />
                  AI-generert
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500">{wordCount} ord</span>
                <button
                  onClick={() => setShowPreview(!showPreview)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                    showPreview
                      ? 'bg-gray-900 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <Eye className="w-3.5 h-3.5 inline mr-1" />
                  {showPreview ? 'Rediger' : 'Forhåndsvis'}
                </button>
                <button
                  onClick={copyToClipboard}
                  className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-xs font-medium transition-colors"
                >
                  {copied ? (
                    <><Check className="w-3.5 h-3.5 inline mr-1" />Kopiert</>
                  ) : (
                    <><Copy className="w-3.5 h-3.5 inline mr-1" />Kopier</>
                  )}
                </button>
              </div>
            </div>
            
            {/* Editor or Preview */}
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
              {showPreview ? (
                <div className="p-6 prose prose-sm max-w-none">
                  <div className="whitespace-pre-wrap text-sm text-gray-700 leading-relaxed">
                    {editedDraft}
                  </div>
                </div>
              ) : (
                <textarea
                  value={editedDraft}
                  onChange={(e) => setEditedDraft(e.target.value)}
                  className="w-full h-96 p-6 focus:outline-none resize-none text-sm text-gray-700 leading-relaxed"
                  placeholder="Rediger svaret her..."
                />
              )}
            </div>
            
            {/* Tips */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <p className="text-sm text-blue-900">
                <strong className="font-semibold">Tips:</strong> Les gjennom svaret og gjør eventuelle justeringer før du sender til kunden. 
                Du kan endre tone, legge til detaljer, eller omformulere deler av teksten.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Actions Footer */}
      {generated && (
        <div className="border-t border-gray-200 p-6 bg-white flex gap-3">
          <button
            onClick={() => {
              setGenerated(false);
              setDraft('');
              setEditedDraft('');
            }}
            className="flex-1 px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-900 font-semibold rounded-xl transition-colors inline-flex items-center justify-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Generer ny
          </button>
          <button
            onClick={sendAnswer}
            disabled={sending || !editedDraft.trim()}
            className="flex-1 px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
          >
            {sending ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Sender...
              </>
            ) : (
              <>
                <Send className="w-4 h-4" />
                Godkjenn og send til kunde
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default AIDraftGenerator;
